import { css, styled } from 'next-yak';

import {
  cornerRadiusXL,
  scale005,
  scale010,
  scale060,
  scale070,
  scale100,
  spaceS,
  spaceXXS,
  xdlColorAIGradient01,
  xdlColorAIGradient02,
  xdlColorAIGradient03,
  xdlColorBackgroundTertiary,
  xdlColorControlFillDark,
  xdlColorControlFillLight,
  xdlColorFocusRing,
  xdlColorMarkerHighlight,
  xdlColorMarkerHighlightText,
  xdlColorMarkerMatch,
  xdlColorMarkerMatchText,
  xdlColorMarkerStrong,
  xdlColorText,
  xdlColorTextOnDark,
  xdlColorTextOnLight,
} from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import type { MarkerVariant } from '.';

type MarkerTProps = {
  $ellipsis?: boolean;
  $variant?: MarkerVariant;
};

type IconContainerTProps = {
  $iconAfter?: boolean;
  $variant?: MarkerVariant;
};

type TextTProps = {
  $ellipsis?: boolean;
  $iconAfter?: boolean;
  $maxCharacters: string;
  $variant?: MarkerVariant;
};

// *** Variants ***
export const variants = {
  default: {
    marker: css`
      background: ${xdlColorBackgroundTertiary};
    `,
    text: css`
      color: ${xdlColorText};
    `,
  },
  match: {
    marker: css`
      background: ${xdlColorMarkerMatch};
    `,
    text: css`
      color: ${xdlColorMarkerMatchText};
    `,
  },
  highlight: {
    marker: css`
      background: ${xdlColorMarkerHighlight};
    `,
    text: css`
      color: ${xdlColorMarkerHighlightText};
    `,
  },
  strong: {
    marker: css`
      background: ${xdlColorMarkerStrong};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  onColor: {
    marker: css`
      background: ${xdlColorControlFillLight};
    `,
    text: css`
      color: ${xdlColorTextOnLight};
    `,
  },
  onColorStrong: {
    marker: css`
      background: ${xdlColorControlFillDark};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  ai: {
    marker: css`
      background:
        radial-gradient(
          74.24% 367.5% at 8.59% 87.5%,
          ${xdlColorAIGradient03} 0%,
          ${xdlColorAIGradient02} 100%
        ),
        radial-gradient(
          41.7% 190% at 93.43% 10%,
          ${xdlColorAIGradient01} 0%,
          ${xdlColorAIGradient02} 100%
        );
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
};

// *** Components ***
export const Marker = styled.span<MarkerTProps>`
  align-items: center;
  border-radius: ${cornerRadiusXL};
  display: inline-flex;
  height: ${scale100};
  line-height: 1.25;
  padding-block-end: ${scale005};
  padding-block-start: ${spaceXXS};
  padding-inline-end: ${spaceS};
  padding-inline-start: ${spaceS};

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'strong':
        return variants.strong.marker;
      case 'default':
        return variants.default.marker;
      case 'match':
        return variants.match.marker;
      case 'highlight':
        return variants.highlight.marker;
      case 'onColor':
        return variants.onColor.marker;
      case 'onColorStrong':
        return variants.onColorStrong.marker;
      case 'ai':
        return variants.ai.marker;
      default:
        return $variant satisfies undefined;
    }
  }};

  ${({ $ellipsis }) => {
    return (
      $ellipsis &&
      css`
        overflow: hidden;
        white-space: nowrap;
      `
    );
  }};
`;

export const IconContainer = styled.span<IconContainerTProps>`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  height: ${scale070};

  ${({ $variant }) => {
    switch ($variant) {
      case 'strong':
        return variants.strong.text;
      case 'default':
        return variants.default.text;
      case 'match':
        return variants.match.text;
      case 'highlight':
        return variants.highlight.text;
      case 'onColor':
        return variants.onColor.text;
      case 'onColorStrong':
        return variants.onColorStrong.text;
      case 'ai':
        return variants.ai.text;
      default:
        return $variant satisfies undefined;
    }
  }};

  ${({ $iconAfter }) => {
    return $iconAfter
      ? css`
          margin-inline-start: ${spaceXXS};
          margin-inline-end: -${spaceXXS};
          order: 1;
        `
      : css`
          margin-inline-end: ${spaceXXS};
          margin-inline-start: -${spaceXXS};
        `;
  }}
`;

export const Text = styled(BodyCopy)<TextTProps>`
  ${({ $ellipsis, $maxCharacters }) => {
    if (!$ellipsis || !$maxCharacters) return;
    return css`
      display: block;
      max-width: ${() => $maxCharacters}ch;
      overflow: hidden;
      text-overflow: ellipsis;
    `;
  }};

  ${({ $variant }) => {
    switch ($variant) {
      case 'strong':
        return css`
          ${variants.strong.text}
          font-size: ${scale060};
          font-weight: bold;
        `;
      case 'default':
        return variants.default.text;
      case 'match':
        return variants.match.text;
      case 'highlight':
        return variants.highlight.text;
      case 'onColor':
        return variants.onColor.text;
      case 'onColorStrong':
        return css`
          ${variants.onColorStrong.text}
          font-size: ${scale060};
          font-weight: bold;
        `;
      case 'ai':
        return css`
          ${variants.ai.text}
          font-size: ${scale060};
          font-weight: bold;
        `;
      default:
        return $variant satisfies undefined;
    }
  }};
`;
